import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Plus, Settings as SettingsIcon } from "lucide-react";

import { type Workspace } from "@/api";
import { useActiveWorkspace } from "@/hooks/use-active-workspace";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import { AddWorkspaceModal } from "@/components/workspaces/add-workspace-modal";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";

interface WorkspaceSelectorProps {
  /**
   * When true the trigger collapses to a single-letter avatar
   * (matches the collapsed sidebar rail and the mobile top bar).
   * The dropdown panel itself is unchanged.
   */
  compact?: boolean;
}

/**
 * Workspace switcher dropdown. Lives at the top of the sidebar,
 * matching the Claude / Linear convention. Switching calls
 * ``useAuth.switchWorkspace`` (via the active-workspace context)
 * which rotates the JWT pair to a fresh token scoped to the new
 * workspace.
 *
 * Sesi WORKSPACE-MGMT (v0.29.1): dropdown gains two extra items
 * at the bottom — "Buat workspace baru" opens an inline modal,
 * and "Kelola workspace" links to ``/workspaces`` for full
 * management. Source of truth for the cached workspace list is
 * ``ActiveWorkspaceContext`` so the dropdown + the management
 * page never disagree.
 */
export function WorkspaceSelector({ compact = false }: WorkspaceSelectorProps = {}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const {
    workspaces,
    activeWorkspace,
    setActiveWorkspaceId,
    refreshWorkspaces,
    isLoading,
  } = useActiveWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const handleSwitch = async (ws: Workspace) => {
    if (ws.id === user?.workspace_id) {
      setIsOpen(false);
      return;
    }
    setIsSwitching(true);
    try {
      await setActiveWorkspaceId(ws.id);
    } finally {
      setIsSwitching(false);
      setIsOpen(false);
    }
  };

  if (isLoading && workspaces.length === 0) {
    if (compact) {
      return (
        <div
          className="flex items-center justify-center rounded-md border border-ocean-100 bg-white p-2"
          aria-label={t("workspaces.selector.loadingAriaLabel")}
        >
          <LoadingSpinner size={14} />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 rounded-md border border-ocean-100 bg-white px-3 py-2 text-sm text-neutral-500">
        <LoadingSpinner size={14} />
        {t("workspaces.selector.loading")}
      </div>
    );
  }

  const isTenantAdmin =
    user?.role === "admin" || user?.role === "super_admin";

  if (workspaces.length === 0) {
    // No workspaces yet — still let an admin create the first one
    // straight from the rail, the way you'd expect from a "no
    // tenants" empty state in any Console-style product.
    return (
      <>
        {isTenantAdmin ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className={cn(
              "flex w-full items-center gap-2 rounded-md border border-dashed border-ocean-200 bg-white px-3 py-2 text-sm text-ocean-700 transition-colors hover:border-ocean-300 hover:bg-ocean-50",
              compact && "justify-center px-2"
            )}
            aria-label={t("workspaces.selector.createFirstAriaLabel")}
          >
            <Plus size={14} className="shrink-0" />
            {!compact && <span>{t("workspaces.selector.createFirst")}</span>}
          </button>
        ) : (
          <div
            className={cn(
              "rounded-md border border-ocean-100 bg-white px-3 py-2 text-sm text-neutral-500",
              compact && "text-center"
            )}
          >
            {compact
              ? t("workspaces.selector.emptyCompact")
              : t("workspaces.selector.emptyNonAdmin")}
          </div>
        )}
        <AddWorkspaceModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onCreated={async (ws) => {
            setAddOpen(false);
            await refreshWorkspaces();
            await setActiveWorkspaceId(ws.id);
          }}
        />
      </>
    );
  }

  const currentInitial =
    activeWorkspace?.name?.trim()?.[0]?.toUpperCase() ?? "W";

  return (
    <>
      <div ref={containerRef} className="relative">
        {compact ? (
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            disabled={isSwitching}
            title={
              activeWorkspace?.name ?? t("workspaces.selector.placeholder")
            }
            aria-label={t("workspaces.selector.triggerAriaLabel", {
              name:
                activeWorkspace?.name ??
                t("workspaces.selector.triggerUnselected"),
            })}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            className={cn(
              "flex w-full items-center justify-center rounded-md border border-ocean-100 bg-white p-2 transition-colors hover:border-ocean-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400 focus-visible:ring-offset-2",
              isSwitching && "opacity-60"
            )}
          >
            {isSwitching ? (
              <LoadingSpinner size={14} />
            ) : (
              <span className="flex h-6 w-6 items-center justify-center rounded bg-ocean-200 text-xs font-medium text-ocean-800">
                {currentInitial}
              </span>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            disabled={isSwitching}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-md border border-ocean-100 bg-white px-3 py-2 text-sm transition-colors hover:border-ocean-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400 focus-visible:ring-offset-2",
              isSwitching && "opacity-60"
            )}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className="min-w-0 flex-1 truncate text-left font-medium text-ocean-800">
              {activeWorkspace?.name ?? t("workspaces.selector.placeholder")}
            </span>
            {isSwitching ? (
              <LoadingSpinner size={14} />
            ) : (
              <ChevronDown
                size={14}
                className={cn(
                  "shrink-0 text-ocean-400 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            )}
          </button>
        )}

        {isOpen && (
          <div
            className={cn(
              "absolute top-full z-20 mt-1 overflow-hidden rounded-md border border-ocean-100 bg-white shadow-sm",
              compact ? "left-0 min-w-[240px]" : "left-0 right-0"
            )}
          >
            <ul role="listbox" className="max-h-64 overflow-y-auto">
              {workspaces.map((ws) => {
                const active = ws.id === user?.workspace_id;
                return (
                  <li key={ws.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => void handleSwitch(ws)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-ocean-50",
                        active && "bg-ocean-50"
                      )}
                    >
                      <div className="min-w-0">
                        <div
                          className={cn(
                            "truncate text-ocean-800",
                            active && "font-medium"
                          )}
                        >
                          {ws.name}
                        </div>
                        <div className="truncate text-xs text-neutral-500">
                          {ws.slug}
                          {ws.is_default &&
                            ` ${t("workspaces.selector.defaultSuffix")}`}
                        </div>
                      </div>
                      {active && (
                        <Check size={14} className="text-ocean-600" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-ocean-100">
              {isTenantAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setAddOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ocean-700 transition-colors hover:bg-ocean-50"
                >
                  <Plus size={14} className="shrink-0" />
                  {t("workspaces.selector.createNew")}
                </button>
              )}
              <Link
                to={ROUTES.WORKSPACES}
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ocean-700 transition-colors hover:bg-ocean-50"
              >
                <SettingsIcon size={14} className="shrink-0" />
                {t("workspaces.selector.manage")}
              </Link>
            </div>
          </div>
        )}
      </div>

      <AddWorkspaceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={async (ws) => {
          setAddOpen(false);
          await refreshWorkspaces();
          await setActiveWorkspaceId(ws.id);
        }}
      />
    </>
  );
}
